import * as baseContext from "@repo/helpers/src/contexts/editableItemContext";
import { EditableItemProviderProps, getEditableItemContext } from "@repo/helpers/src/contexts/editableItemContext";
import { ComplaintCustomerAction, RepFieldAudit } from "@repo/models";
import { Context, useContext } from "react";

import {
	BackgroundCheck,
	Complaint,
	Documentation,
	DrugTest,
	EnrollmentValidationError,
	Rep,
	RepDiscipline,
	RepOnboardingAudit,
} from "../models";
import { ITrainerCertification, MVCStateSpecificTraining } from "../models/MVCStateSpecificTraining";
import { RepDeactivationDetails } from "../models/repDeactivationDetails";
import { RepTraining } from "../models/repSegment";
import { itemType } from "./pendingChangesContext";

export const RepContext = getEditableItemContext<Rep>();
export const BackgroundCheckContext = getEditableItemContext<BackgroundCheck>();
export const DrugTestsContext = getEditableItemContext<DrugTest>();
export const RepDisciplineContext = getEditableItemContext<RepDiscipline>();
export const DocumentationContext = getEditableItemContext<Documentation>();
export const EVEContext = getEditableItemContext<EnrollmentValidationError>();
export const ComplaintContext = getEditableItemContext<Complaint>();
export const ComplaintCustomerActionContext = getEditableItemContext<ComplaintCustomerAction>();
export const RepTrainingContext = getEditableItemContext<RepTraining>();
export const RepDeactivationDetailsContext = getEditableItemContext<RepDeactivationDetails>();
export const StateSpecificTrainingContext = getEditableItemContext<MVCStateSpecificTraining>();
export const TrainerCertificationContext = getEditableItemContext<ITrainerCertification>();
export const RepOnboardingAuditContext = getEditableItemContext<RepOnboardingAudit>();
export const RepFieldAuditContext = getEditableItemContext<RepFieldAudit>();

const emptyRep = {} as Rep;

export function useRepContext() {
	const context = useContext(RepContext);
	const rep = context.item ?? emptyRep;
	const savedRep = context.savedItem ?? emptyRep;
	const repId = rep.repId;
	const repLoaded = context.item && (!repId || Object.keys(rep).length > 1);
	return { ...context, rep, savedRep, repId, repLoaded };
}

export function useBackgroundCheckContext() {
	return useContext(BackgroundCheckContext);
}

export function useDrugTestsContext() {
	return useContext(DrugTestsContext);
}

export function useRepDisciplineContext() {
	return useContext(RepDisciplineContext);
}

export function useDocumentationContext() {
	return useContext(DocumentationContext);
}

export function useEVEContext() {
	return useContext(EVEContext);
}

export function useComplaintContext() {
	return useContext(ComplaintContext);
}

export function useComplaintCustomerActionContext() {
	return useContext(ComplaintCustomerActionContext);
}

export function useRepTrainingContext() {
	return useContext(RepTrainingContext);
}

export function useStateSpecificTrainingContext() {
	return useContext(StateSpecificTrainingContext);
}

export function useTrainerCertificationContext() {
	return useContext(TrainerCertificationContext);
}

export function useRepDeactivationDetailsContext() {
	return useContext(RepDeactivationDetailsContext);
}

export function useRepOnboardingAuditContext() {
	return useContext(RepOnboardingAuditContext);
}

export function useRepFieldAuditContext() {
	return useContext(RepFieldAuditContext);
}

export const RepProvider = getProvider(RepContext, "rep");
export const BackgroundCheckProvider = getProvider(BackgroundCheckContext, "bgc");
export const DrugTestsProvider = getProvider(DrugTestsContext, "drugTest");
export const RepDisciplinesProvider = getProvider(RepDisciplineContext, "repDiscipline");
export const DocumentationProvider = getProvider(DocumentationContext, "documentation");
export const EVEProvider = getProvider(EVEContext, "eve");
export const ComplaintProvider = getProvider(ComplaintContext, "complaint");
export const ComplaintCustomerActionProvider = getProvider(ComplaintCustomerActionContext, "complaintCustomerAction");
export const RepTrainingProvider = getProvider(RepTrainingContext, "repTraining");
export const StateSpecificTrainingProvider = getProvider(StateSpecificTrainingContext, "stateSpecificTraining");
export const TrainerCertificationProvider = getProvider(TrainerCertificationContext, "trainerCertification");
export const RepDeactivationProvider = getProvider(RepDeactivationDetailsContext, "repDeactivationDetails");
export const RepOnboardingAuditProvider = getProvider(RepOnboardingAuditContext, "repOnboardingAudit");
export const RepFieldAuditProvider = getProvider(RepFieldAuditContext, "repFieldAudit");

function getProvider<T>(context: Context<baseContext.EditableItemContextType<T>>, type: itemType) {
	return function (props: EditableItemProviderProps<T>) {
		return baseContext.EditableItemProvider(context, props, type);
	};
}
